/* eslint-disable no-eval */
/* eslint-disable no-shadow */
/* eslint-disable prefer-destructuring */
/* eslint-disable no-unused-vars */
/* eslint-disable no-param-reassign */
/* eslint-disable react/no-array-index-key */
import React, { useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  useTheme,
} from "@mui/material";
import { NavigateBefore } from "@mui/icons-material";

// Existing step components
import CreateProspectMapper from "./config-generator/CreateProspectMapper";
import EnrichConsumerMapper from "./config-generator/EnrichConsumerMapper";
import DataSelector from "./config-generator/DataSelector";
import ConfigDetails from "./config-generator/ConfigDetails";
import SchemaGenerator from "./config-generator/SchemaGenerator";
import IngestionMapper from "./config-generator/IngestionMapper";
import ReviewComponent from "./config-generator/ReviewComponent";

import {
  steps,
  prospectSchemaFields,
  prospectArrayFields,
  ajvFormatOptions,
} from "./config-generator/constants";
import {
  useBatchConfigState,
  useStepperNavigation,
  useFormManager,
} from "./config-generator/hooks/useBatchConfigState";
import { useMapperManager } from "./config-generator/hooks/useMapperManager";
import {
  initializeIngestionMappings,
  initializeAjvSchemas,
  getArrayObjectTemplate,
} from "./config-generator/utils/mappingUtils";
import {
  handleProspectDragEnd,
  handleEnrichConsumerDragEnd,
  updateAvailableIngestionFields,
} from "./config-generator/utils/dragDropUtils";
import { ConfigurationService } from "./config-generator/services/ConfigurationService";

// Modal components
import AddDefaultModal from "./config-generator/modals/AddDefaultModal";
import EnrichmentModal from "./config-generator/modals/EnrichmentModal";
import ModifierModal from "./config-generator/modals/ModifierModal";
import CommPrefModal from "./config-generator/modals/CommPrefModal";
import ConsentModal from "./config-generator/modals/ConsentModal";
import PreviewModal from "./config-generator/modals/PreviewModal";
import LoadConfigModal from "./config-generator/modals/LoadConfigModal";

import { useUser } from "../context/UserContext";

const BatchConfigV2 = ({ onFileUpload }) => {
  const theme = useTheme();
  const { user } = useUser();

  // Main state management
  const state = useBatchConfigState();
  const {
    activeStep,
    setActiveStep,
    filePreview,
    setFilePreview,
    fileMetadata,
    setFileMetadata,
    formValues,
    setFormValues,
    ageRanges,
    setAgeRanges,
    selectedRange,
    setSelectedRange,
    newCountryCode,
    setNewCountryCode,
    ajvSchemas,
    setAjvSchemas,
    selectedSchemaMarket,
    setSelectedSchemaMarket,
    selectedAttribute,
    setSelectedAttribute,
    newSchemaCode,
    setNewSchemaCode,
    enumInputValue,
    setEnumInputValue,
    mappingDrafts,
    setMappingDrafts,
    ingestionMappings,
    setIngestionMappings,
    prospectMappings,
    setProspectMappings,
    availableIngestionFields,
    setAvailableIngestionFields,
    enrichConsumerMappings,
    setEnrichConsumerMappings,
    availableIngestionFieldsEnrich,
    setAvailableIngestionFieldsEnrich,
    arrayDefaults,
    setArrayDefaults,
    addDefaultModal,
    setAddDefaultModal,
    enrichmentModal,
    setEnrichmentModal,
    modifierModal,
    setModifierModal,
    commPrefModal,
    setCommPrefModal,
    consentModal,
    setConsentModal,
    previewModal,
    setPreviewModal,
    searchModal,
    setSearchModal,
    exportStatus,
    setExportStatus,
  } = state;

  // Mapper management
  const { mappers } = useMapperManager(
    mappingDrafts,
    ingestionMappings,
    prospectMappings,
    enrichConsumerMappings,
    arrayDefaults,
    formValues
  );

  // Navigation management
  const { handleNext, handleBack } = useStepperNavigation(activeStep, setActiveStep, steps.length);

  // Form management
  const { handleFormValueChange } = useFormManager(formValues, setFormValues);

  // File handling
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Store file metadata
    setFileMetadata({
      name: file.name,
      size: file.size,
      type: file.type || (file.name.endsWith(".csv") ? "text/csv" : "application/json"),
      lastModified: file.lastModified,
    });

    // Notify parent that file has been uploaded
    onFileUpload?.(true);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const content = ev.target.result;
      if (file.name.endsWith(".json") || file.type === "application/json") {
        try {
          let data = JSON.parse(content);
          if (Array.isArray(data)) {
            const [firstRecord] = data;
            data = firstRecord;
          }
          setFilePreview(data);
          // Initialize ingestion mappings with source attributes
          setIngestionMappings(initializeIngestionMappings(Object.keys(data), formValues));
          // Initialize AJV schemas based on inferred types
          setAjvSchemas(initializeAjvSchemas(data));
        } catch {
          setFilePreview({ error: "Invalid JSON" });
        }
      } else {
        const lines = content.split(/\r?\n/).filter((l) => l.trim());
        if (lines.length > 1) {
          const headers = lines[0].split(",");
          const values = lines[1].split(",");
          const obj = {};
          headers.forEach((h, i) => {
            obj[h.trim()] = (values[i] || "").trim();
          });
          setFilePreview(obj);
          // Initialize ingestion mappings with source attributes
          setIngestionMappings(
            initializeIngestionMappings(
              headers.map((h) => h.trim()),
              formValues
            )
          );
          // Initialize AJV schemas based on inferred types
          setAjvSchemas(initializeAjvSchemas(obj));
        }
      }
    };
    reader.readAsText(file);
  };

  // Age range management
  const handleRangeSelect = (event, value) => {
    if (!value) return;
    setSelectedRange(value);
  };

  const handleAgeChange = (field, value) => {
    setAgeRanges((prev) => ({
      ...prev,
      [selectedRange]: { ...prev[selectedRange], [field]: Number(value) },
    }));
  };

  const addCountryRange = () => {
    if (!newCountryCode) return;
    // Copy the default age range values to the new country
    const defaultValues = ageRanges.default || { min: 18, max: 120 };
    setAgeRanges((prev) => ({
      ...prev,
      [newCountryCode]: { min: defaultValues.min, max: defaultValues.max },
    }));
    setSelectedRange(newCountryCode);
    setNewCountryCode("");
  };

  // Schema management
  const handleSchemaMarketSelect = (event, value) => {
    if (!value) return;
    setSelectedSchemaMarket(value);
    setSelectedAttribute("");
  };

  const handleAttributeSelect = (attr) => {
    setSelectedAttribute(attr);
    // Clear enum input when switching attributes
    setEnumInputValue("");
    setAjvSchemas((prev) => {
      const marketSchema = prev[selectedSchemaMarket];
      const properties = { ...marketSchema.properties };
      const required = [...marketSchema.required];

      if (!properties[attr]) {
        properties[attr] = { type: "string" };
        // Add to required array if not already present
        if (!required.includes(attr)) {
          required.push(attr);
        }
      }

      return {
        ...prev,
        [selectedSchemaMarket]: {
          ...marketSchema,
          properties,
          required,
        },
      };
    });
  };

  const handlePropertyChange = (field, value) => {
    setAjvSchemas((prev) => {
      const marketSchema = prev[selectedSchemaMarket];
      const properties = { ...marketSchema.properties };
      const prop = { ...properties[selectedAttribute], [field]: value };
      properties[selectedAttribute] = prop;
      return { ...prev, [selectedSchemaMarket]: { ...marketSchema, properties } };
    });
  };

  const toggleRequired = (attr) => {
    setAjvSchemas((prev) => {
      const marketSchema = prev[selectedSchemaMarket];
      const req = new Set(marketSchema.required);
      if (req.has(attr)) req.delete(attr);
      else req.add(attr);
      return { ...prev, [selectedSchemaMarket]: { ...marketSchema, required: Array.from(req) } };
    });
  };

  const addSchemaMarket = () => {
    if (!newSchemaCode) return;
    // Copy the default schema structure to the new market
    const defaultSchema = ajvSchemas.default || { type: "object", required: [], properties: {} };
    setAjvSchemas((prev) => ({
      ...prev,
      [newSchemaCode]: {
        type: defaultSchema.type,
        required: [...defaultSchema.required],
        properties: JSON.parse(JSON.stringify(defaultSchema.properties)),
      },
    }));
    setSelectedSchemaMarket(newSchemaCode);
    setNewSchemaCode("");
  };

  // Enum management
  const addEnumValue = (value) => {
    if (!value.trim()) return;
    const currentEnum = ajvSchemas[selectedSchemaMarket].properties[selectedAttribute]?.enum || [];
    if (!currentEnum.includes(value.trim())) {
      handlePropertyChange("enum", [...currentEnum, value.trim()]);
    }
    setEnumInputValue("");
  };

  const removeEnumValue = (valueToRemove) => {
    const currentEnum = ajvSchemas[selectedSchemaMarket].properties[selectedAttribute]?.enum || [];
    handlePropertyChange(
      "enum",
      currentEnum.filter((value) => value !== valueToRemove)
    );
  };

  const handleEnumInputKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addEnumValue(enumInputValue);
    }
  };

  // Drag and drop handlers
  const handleProspectDragEndWrapper = (result) => {
    handleProspectDragEnd(
      result,
      availableIngestionFields,
      setAvailableIngestionFields,
      prospectMappings,
      setProspectMappings,
      (arrayField, sourceField) =>
        getArrayObjectTemplate(arrayField, sourceField, formValues.partnerName || "partner")
    );
  };

  const handleEnrichConsumerDragEndWrapper = (result) => {
    handleEnrichConsumerDragEnd(
      result,
      availableIngestionFieldsEnrich,
      setAvailableIngestionFieldsEnrich,
      enrichConsumerMappings,
      setEnrichConsumerMappings,
      (arrayField, sourceField) =>
        getArrayObjectTemplate(arrayField, sourceField, formValues.partnerName || "partner")
    );
  };

  // Update available ingestion fields when ingestion mappings change
  useEffect(() => {
    updateAvailableIngestionFields(
      ingestionMappings,
      prospectMappings,
      enrichConsumerMappings,
      setAvailableIngestionFields,
      setAvailableIngestionFieldsEnrich
    );
  }, [ingestionMappings, prospectMappings, enrichConsumerMappings]);

  // Array defaults management
  const addArrayDefault = (
    arrayField,
    defaultValue,
    granted = true,
    category = "default",
    enrichmentType = "custom",
    commPreferenceType = "",
    mapperType = "createProspect"
  ) => {
    const item = { value: defaultValue, granted };
    if (arrayField === "enrichmentData") {
      item.category = category;
      item.enrichmentType = enrichmentType;
    }
    if (arrayField === "commPreferences" && commPreferenceType) {
      item.commPreferenceType = commPreferenceType;
    }
    setArrayDefaults((prev) => ({
      ...prev,
      [mapperType]: {
        ...prev[mapperType],
        [arrayField]: [...prev[mapperType][arrayField], item],
      },
    }));
  };

  const removeArrayDefault = (arrayField, index, mapperType = "createProspect") => {
    setArrayDefaults((prev) => ({
      ...prev,
      [mapperType]: {
        ...prev[mapperType],
        [arrayField]: prev[mapperType][arrayField].filter((_, i) => i !== index),
      },
    }));
  };

  // Configuration management
  const exportConfiguration = async () => {
    setExportStatus({ loading: true, success: false, error: null });

    const result = await ConfigurationService.exportConfiguration(
      formValues,
      ageRanges,
      ajvSchemas,
      mappers,
      user,
      filePreview,
      fileMetadata,
      selectedRange,
      selectedSchemaMarket,
      selectedAttribute,
      ingestionMappings,
      prospectMappings,
      enrichConsumerMappings,
      arrayDefaults,
      availableIngestionFields,
      availableIngestionFieldsEnrich
    );

    if (result.success) {
      setExportStatus({ loading: false, success: true, error: null });
      setTimeout(() => {
        setExportStatus({ loading: false, success: false, error: null });
      }, 3000);
    } else {
      setExportStatus({ loading: false, success: false, error: result.error });
    }
  };

  const handleSearchConfigurations = async () => {
    setSearchModal((prev) => ({ ...prev, loading: true, error: null }));

    const result = await ConfigurationService.loadConfigurations(user.region);

    if (result.success) {
      setSearchModal((prev) => ({
        ...prev,
        loading: false,
        results: result.data,
      }));
    } else {
      setSearchModal((prev) => ({
        ...prev,
        loading: false,
        error: result.error,
      }));
    }
  };

  const handleLoadConfiguration = async (configId) => {
    setSearchModal((prev) => ({ ...prev, loading: true, error: null }));

    const result = await ConfigurationService.loadConfiguration(configId);

    if (result.success) {
      const config = result.data;

      // Load configuration data into state
      setFormValues({
        partnerName: config.partnerName || "",
        countryAttribute: config.countryHeader || "",
        dobAttribute: config.dobHeader || "",
        hashIdentifier: config.hashIdentifier || "email",
      });

      setAgeRanges(config.ageRanges || { default: { min: 18, max: 120 } });
      setAjvSchemas(
        config.ajvSchemas || { default: { type: "object", required: [], properties: {} } }
      );

      // Restore UI state if available
      if (config.uiState) {
        const { uiState } = config;

        if (uiState.filePreview) setFilePreview(uiState.filePreview);
        if (uiState.fileMetadata) setFileMetadata(uiState.fileMetadata);
        if (uiState.formValues) setFormValues(uiState.formValues);
        if (uiState.selectedRange) setSelectedRange(uiState.selectedRange);
        if (uiState.selectedSchemaMarket) setSelectedSchemaMarket(uiState.selectedSchemaMarket);
        if (uiState.selectedAttribute) setSelectedAttribute(uiState.selectedAttribute);
        if (uiState.ingestionMappings) setIngestionMappings(uiState.ingestionMappings);
        if (uiState.prospectMappings) setProspectMappings(uiState.prospectMappings);
        if (uiState.enrichConsumerMappings) {
          setEnrichConsumerMappings(uiState.enrichConsumerMappings);
        }
        if (uiState.arrayDefaults) setArrayDefaults(uiState.arrayDefaults);
        if (uiState.availableIngestionFields) {
          setAvailableIngestionFields(uiState.availableIngestionFields);
        }
        if (uiState.availableIngestionFieldsEnrich) {
          setAvailableIngestionFieldsEnrich(uiState.availableIngestionFieldsEnrich);
        }
      }

      // Close modal and show success
      setSearchModal({
        open: false,
        searchTerm: "",
        results: [],
        loading: false,
        error: null,
        selectedConfigId: "",
      });

      setExportStatus({ loading: false, success: true, error: null });
      setTimeout(() => {
        setExportStatus({ loading: false, success: false, error: null });
      }, 3000);
    } else {
      setSearchModal((prev) => ({
        ...prev,
        loading: false,
        error: result.error,
      }));
    }
  };

  const handleOpenSearchModal = () => {
    setSearchModal((prev) => ({ ...prev, open: true }));
    handleSearchConfigurations();
  };

  const handleSearchModalClose = () => {
    setSearchModal({
      open: false,
      searchTerm: "",
      results: [],
      loading: false,
      error: null,
      selectedConfigId: "",
    });
  };

  // Preview functionality
  const previewProspectMapping = () => {
    const result = ConfigurationService.previewMapping(
      filePreview,
      formValues,
      ageRanges,
      ajvSchemas,
      mappers,
      user,
      selectedRange,
      selectedSchemaMarket,
      selectedAttribute,
      ingestionMappings,
      prospectMappings,
      enrichConsumerMappings,
      arrayDefaults,
      availableIngestionFields,
      availableIngestionFieldsEnrich
    );

    if (result.success) {
      setPreviewModal({
        open: true,
        title: "Complete Mapping Preview",
        originalData: result.data.originalData,
        mappedData: result.data.mappedData,
        intermediateData: result.data.intermediateData,
      });
    } else {
      setPreviewModal({
        open: true,
        title: "Prospect Mapping Error",
        originalData: result.data?.originalData || null,
        mappedData: result.data?.mappedData || { error: result.error },
        intermediateData: result.data?.intermediateData || null,
      });
    }
  };

  // Modal handlers
  const handleAddDefaultModalSubmit = () => {
    if (addDefaultModal.value.trim()) {
      addArrayDefault(
        addDefaultModal.arrayField,
        addDefaultModal.value.trim(),
        addDefaultModal.granted,
        addDefaultModal.category,
        addDefaultModal.enrichmentType,
        addDefaultModal.commPreferenceType,
        addDefaultModal.mapperType
      );
      setAddDefaultModal({
        open: false,
        arrayField: "",
        value: "",
        granted: true,
        category: "default",
        enrichmentType: "custom",
        commPreferenceType: "",
        mapperType: "createProspect",
      });
    }
  };

  const handleAddDefaultModalClose = () => {
    setAddDefaultModal({
      open: false,
      arrayField: "",
      value: "",
      granted: true,
      category: "default",
      enrichmentType: "custom",
      commPreferenceType: "",
      mapperType: "createProspect",
    });
  };

  const handleEnrichmentModalSubmit = () => {
    const mapping = prospectMappings[enrichmentModal.mappingIndex];
    if (mapping) {
      const updatedMappings = [...prospectMappings];
      updatedMappings[enrichmentModal.mappingIndex] = {
        ...mapping,
        modifier: `(val, src) => [{ enrichmentValue: val, category: "${enrichmentModal.category}", enrichmentType: "${enrichmentModal.enrichmentType}", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${formValues.partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${formValues.partnerName}" }]`,
      };
      setProspectMappings(updatedMappings);
    }
    setEnrichmentModal({
      open: false,
      mappingIndex: -1,
      category: "default",
      enrichmentType: "custom",
    });
  };

  const handleEnrichmentModalClose = () => {
    setEnrichmentModal({
      open: false,
      mappingIndex: -1,
      category: "default",
      enrichmentType: "custom",
    });
  };

  const handleModifierModalSubmit = () => {
    const mapping = prospectMappings[modifierModal.mappingIndex];
    if (mapping && modifierModal.modifier.trim()) {
      const updatedMappings = [...prospectMappings];
      updatedMappings[modifierModal.mappingIndex] = {
        ...mapping,
        modifier: modifierModal.modifier.trim(),
      };
      setProspectMappings(updatedMappings);
    }
    setModifierModal({ open: false, mappingIndex: -1, modifier: "" });
  };

  const handleModifierModalClose = () => {
    setModifierModal({ open: false, mappingIndex: -1, modifier: "" });
  };

  const handleCommPrefModalSubmit = () => {
    if (!commPrefModal.channel.trim() || !commPrefModal.commPreferenceType.trim()) return;

    const modifierFunction = `(val, src) => [{ commPreferenceType: "${commPrefModal.commPreferenceType.trim()}", createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${formValues.partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${formValues.partnerName}", channel: "${commPrefModal.channel.trim()}", granted: 1 }]`;

    if (commPrefModal.mapperType === "enrichConsumer") {
      const mapping = enrichConsumerMappings[commPrefModal.mappingIndex];
      if (mapping) {
        const updatedMappings = [...enrichConsumerMappings];
        updatedMappings[commPrefModal.mappingIndex] = {
          ...mapping,
          channel: commPrefModal.channel.trim(),
          modifier: modifierFunction,
        };
        setEnrichConsumerMappings(updatedMappings);
      }
    } else {
      const mapping = prospectMappings[commPrefModal.mappingIndex];
      if (mapping) {
        const updatedMappings = [...prospectMappings];
        updatedMappings[commPrefModal.mappingIndex] = {
          ...mapping,
          channel: commPrefModal.channel.trim(),
          modifier: modifierFunction,
        };
        setProspectMappings(updatedMappings);
      }
    }
    setCommPrefModal({
      open: false,
      mappingIndex: -1,
      channel: "email",
      commPreferenceType: "",
      mapperType: "prospect",
    });
  };

  const handleCommPrefModalClose = () => {
    setCommPrefModal({
      open: false,
      mappingIndex: -1,
      channel: "email",
      commPreferenceType: "",
      mapperType: "prospect",
    });
  };

  const handleConsentModalSubmit = () => {
    if (!consentModal.consentType.trim()) return;

    const partnerName = formValues.partnerName || "partner";
    const modifierFunction = `(val, src) => [{ createdAt: (src && src.createdAt) || new Date().toISOString(), createdBy: "${partnerName}", updatedAt: (src && src.createdAt) || new Date().toISOString(), updatedBy: "${partnerName}", consentType: "${consentModal.consentType.trim()}", granted: val === true ? 1 : 0 }]`;

    if (consentModal.mapperType === "enrichConsumer") {
      const mapping = enrichConsumerMappings[consentModal.mappingIndex];
      if (mapping) {
        const updatedMappings = [...enrichConsumerMappings];
        updatedMappings[consentModal.mappingIndex] = {
          ...mapping,
          consentType: consentModal.consentType.trim(),
          modifier: modifierFunction,
        };
        setEnrichConsumerMappings(updatedMappings);
      }
    } else {
      const mapping = prospectMappings[consentModal.mappingIndex];
      if (mapping) {
        const updatedMappings = [...prospectMappings];
        updatedMappings[consentModal.mappingIndex] = {
          ...mapping,
          consentType: consentModal.consentType.trim(),
          modifier: modifierFunction,
        };
        setProspectMappings(updatedMappings);
      }
    }
    setConsentModal({ open: false, mappingIndex: -1, consentType: "", mapperType: "prospect" });
  };

  const handleConsentModalClose = () => {
    setConsentModal({ open: false, mappingIndex: -1, consentType: "", mapperType: "prospect" });
  };

  const handlePreviewModalClose = () => {
    setPreviewModal({
      open: false,
      title: "",
      originalData: null,
      mappedData: null,
      intermediateData: null,
    });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          backgroundColor: theme.palette.background.paper,
          borderRadius: theme.spacing(1),
        }}
      >
        {/* Header Section */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: theme.typography.h4.fontWeight,
              mb: 1,
            }}
          >
            Advanced Configuration Wizard
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              mb: 2,
            }}
          >
            Configure data mapping, validation schemas, and transformation rules for your
            integration
          </Typography>
        </Box>

        <Box sx={{ width: "100%", py: 2 }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              "& .MuiStepIcon-root.Mui-active": {
                color: theme.palette.primary.main,
              },
              "& .MuiStepIcon-root.Mui-completed": {
                color: theme.palette.success.main,
              },
              "& .MuiStepLabel-label": {
                color: theme.palette.text.primary,
                fontFamily: theme.typography.fontFamily,
              },
              "& .MuiStepLabel-label.Mui-active": {
                color: theme.palette.primary.main,
                fontWeight: theme.typography.fontWeightMedium,
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box
            sx={{
              mt: 4,
              p: theme.spacing(2),
              backgroundColor: theme.palette.background.default,
              borderRadius: theme.spacing(1),
              minHeight: "400px",
            }}
          >
            {activeStep === 0 && (
              <DataSelector
                filePreview={filePreview}
                fileMetadata={fileMetadata}
                onFileChange={handleFileChange}
                onLoadConfiguration={handleOpenSearchModal}
              />
            )}
            {activeStep === 1 && (
              <ConfigDetails
                formValues={formValues}
                onFormValueChange={handleFormValueChange}
                filePreview={filePreview}
                fileMetadata={fileMetadata}
                ageRanges={ageRanges}
                selectedRange={selectedRange}
                onRangeSelect={handleRangeSelect}
                onAgeChange={handleAgeChange}
                newCountryCode={newCountryCode}
                onNewCountryCodeChange={setNewCountryCode}
                onAddCountryRange={addCountryRange}
              />
            )}
            {activeStep === 2 && (
              <SchemaGenerator
                selectedSchemaMarket={selectedSchemaMarket}
                onSchemaMarketSelect={handleSchemaMarketSelect}
                newSchemaCode={newSchemaCode}
                onNewSchemaCodeChange={setNewSchemaCode}
                onAddSchemaMarket={addSchemaMarket}
                ajvSchemas={ajvSchemas}
                filePreview={filePreview}
                selectedAttribute={selectedAttribute}
                onAttributeSelect={handleAttributeSelect}
                onPropertyChange={handlePropertyChange}
                ajvFormatOptions={ajvFormatOptions}
                enumInputValue={enumInputValue}
                onEnumInputValueChange={setEnumInputValue}
                onEnumInputKeyPress={handleEnumInputKeyPress}
                onEnumInputBlur={() => setEnumInputValue("")}
                onRemoveEnumValue={removeEnumValue}
                onToggleRequired={toggleRequired}
              />
            )}
            {activeStep === 3 && (
              <IngestionMapper
                ingestionMappings={ingestionMappings}
                onIngestionMappingChange={(sourceKey, field, value) => {
                  setIngestionMappings((prev) => ({
                    ...prev,
                    [sourceKey]: {
                      ...prev[sourceKey],
                      [field]: value,
                    },
                  }));
                }}
              />
            )}
            {activeStep === 4 && (
              <CreateProspectMapper
                availableIngestionFields={availableIngestionFields}
                prospectSchemaFields={prospectSchemaFields}
                prospectArrayFields={prospectArrayFields}
                prospectMappings={prospectMappings}
                arrayDefaults={arrayDefaults.createProspect}
                onDragEnd={handleProspectDragEndWrapper}
                onAddDefault={(field) => {
                  setAddDefaultModal({
                    open: true,
                    arrayField: field,
                    value: "",
                    granted: true,
                    category: "default",
                    enrichmentType: "custom",
                    commPreferenceType: formValues.partnerName || "default",
                    mapperType: "createProspect",
                  });
                }}
                onRemoveArrayDefault={(field, index) =>
                  removeArrayDefault(field, index, "createProspect")
                }
                onDoubleClick={(field, mapping) => {
                  const mappingIndex = prospectMappings.findIndex(
                    (m) => m.prospectField === field && m.ingestionField === mapping.ingestionField
                  );
                  if (field === "enrichmentData") {
                    setEnrichmentModal({
                      open: true,
                      mappingIndex,
                      category: "default",
                      enrichmentType: "custom",
                    });
                  } else if (field === "commPreferences") {
                    setCommPrefModal({
                      open: true,
                      mappingIndex,
                      channel: "email",
                      commPreferenceType: formValues.partnerName || "default",
                      mapperType: "prospect",
                    });
                  } else if (field === "consents") {
                    setConsentModal({
                      open: true,
                      mappingIndex,
                      consentType: mapping.ingestionField || "",
                      mapperType: "prospect",
                    });
                  } else if (!["legalAcceptances"].includes(field)) {
                    setModifierModal({
                      open: true,
                      mappingIndex,
                      modifier: mapping.modifier || "(e) => e",
                    });
                  }
                }}
              />
            )}
            {activeStep === 5 && (
              <EnrichConsumerMapper
                prospectSchemaFields={prospectSchemaFields}
                prospectArrayFields={prospectArrayFields}
                availableIngestionFields={availableIngestionFieldsEnrich}
                enrichConsumerMappings={enrichConsumerMappings}
                arrayDefaults={arrayDefaults.enrichConsumer}
                onDragEnd={handleEnrichConsumerDragEndWrapper}
                onAddDefault={(arrayField) => {
                  setAddDefaultModal({
                    open: true,
                    arrayField,
                    value: "",
                    granted: true,
                    category: "default",
                    enrichmentType: "custom",
                    commPreferenceType: formValues.partnerName || "default",
                    mapperType: "enrichConsumer",
                  });
                }}
                onRemoveArrayDefault={(arrayField, index) =>
                  removeArrayDefault(arrayField, index, "enrichConsumer")
                }
                onDoubleClick={(field, mapping) => {
                  const mappingIndex = enrichConsumerMappings.findIndex(
                    (m) => m.prospectField === field && m.ingestionField === mapping.ingestionField
                  );
                  if (field === "enrichmentData") {
                    setEnrichmentModal({
                      open: true,
                      mappingIndex,
                      category: "default",
                      enrichmentType: "custom",
                    });
                  } else if (field === "commPreferences") {
                    setCommPrefModal({
                      open: true,
                      mappingIndex,
                      channel: "email",
                      commPreferenceType: formValues.partnerName || "default",
                      mapperType: "enrichConsumer",
                    });
                  } else if (field === "consents") {
                    setConsentModal({
                      open: true,
                      mappingIndex,
                      consentType: mapping.ingestionField || "",
                      mapperType: "enrichConsumer",
                    });
                  } else if (!["legalAcceptances"].includes(field)) {
                    setModifierModal({
                      open: true,
                      mappingIndex,
                      modifier: mapping.modifier || "(e) => e",
                    });
                  }
                }}
              />
            )}
            {activeStep === 6 && (
              <ReviewComponent
                formValues={formValues}
                ageRanges={ageRanges}
                ajvSchemas={ajvSchemas}
                ingestionMappings={ingestionMappings}
                prospectMappings={prospectMappings}
                enrichConsumerMappings={enrichConsumerMappings}
                exportStatus={exportStatus}
                onPreviewProspectMapping={previewProspectMapping}
                onExportConfiguration={exportConfiguration}
              />
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 4,
                pt: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              {activeStep === 0 ? (
                <Box />
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<NavigateBefore />}
                  onClick={handleBack}
                  sx={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    fontFamily: theme.typography.fontFamily,
                    textTransform: theme.typography.button.textTransform,
                    "&:hover": {
                      borderColor: theme.palette.primary.dark,
                      backgroundColor: `${theme.palette.primary.main}08`,
                    },
                  }}
                >
                  Back
                </Button>
              )}
              {activeStep < steps.length - 1 && (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={activeStep === 0 && !filePreview}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    fontFamily: theme.typography.fontFamily,
                    textTransform: theme.typography.button.textTransform,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    "&:disabled": {
                      backgroundColor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled,
                    },
                  }}
                >
                  Next
                </Button>
              )}
              {activeStep === steps.length - 1 && <Box />}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Modals */}
      <AddDefaultModal
        open={addDefaultModal.open}
        onClose={handleAddDefaultModalClose}
        onSubmit={handleAddDefaultModalSubmit}
        modalState={addDefaultModal}
        onStateChange={(field, value) =>
          setAddDefaultModal((prev) => ({ ...prev, [field]: value }))
        }
      />

      <EnrichmentModal
        open={enrichmentModal.open}
        onClose={handleEnrichmentModalClose}
        onSubmit={handleEnrichmentModalSubmit}
        modalState={enrichmentModal}
        onStateChange={(field, value) =>
          setEnrichmentModal((prev) => ({ ...prev, [field]: value }))
        }
      />

      <ModifierModal
        open={modifierModal.open}
        onClose={handleModifierModalClose}
        onSubmit={handleModifierModalSubmit}
        modalState={modifierModal}
        onStateChange={(field, value) => setModifierModal((prev) => ({ ...prev, [field]: value }))}
      />

      <CommPrefModal
        open={commPrefModal.open}
        onClose={handleCommPrefModalClose}
        onSubmit={handleCommPrefModalSubmit}
        modalState={commPrefModal}
        onStateChange={(field, value) => setCommPrefModal((prev) => ({ ...prev, [field]: value }))}
      />

      <ConsentModal
        open={consentModal.open}
        onClose={handleConsentModalClose}
        onSubmit={handleConsentModalSubmit}
        modalState={consentModal}
        onStateChange={(field, value) => setConsentModal((prev) => ({ ...prev, [field]: value }))}
      />

      <PreviewModal
        open={previewModal.open}
        onClose={handlePreviewModalClose}
        modalState={previewModal}
      />

      <LoadConfigModal
        open={searchModal.open}
        onClose={handleSearchModalClose}
        onLoad={handleLoadConfiguration}
        modalState={searchModal}
        onStateChange={(field, value) => setSearchModal((prev) => ({ ...prev, [field]: value }))}
      />
    </Container>
  );
};

export default BatchConfigV2;
